import React, { useMemo } from 'react';
import { Box, Stack, Typography, Accordion, AccordionSummary, AccordionDetails } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
    GlColumnDef, GlSheetRowVO, buildColumnKey, mergeGlRow6Display, resolvePriorYearAmount,
} from '@/services/accountService/glInvoiceService';
import { resolveGlCurrentPeriodDisplay } from '@/utils/glToFsMapper';
import {
    buildPpeGroups,
    filterNonEmptyPpeColumns,
    ppeColumnShortLabel,
    PpeGroup,
} from '@/pages/components/account/gl-invoice/mode/tabs/ppeColumnUtils';
import {
    buildExcludedBankSummaryKeys,
    isExRateColumn,
    sumByKeys,
} from '@/pages/components/account/gl-invoice/mode/tabs/bankColumnUtils';
import TinyRowSearch from '@/pages/components/account/gl-invoice/mode/TinyRowSearch';
import { tinyRowMatchesQuery } from '@/pages/components/account/gl-invoice/mode/matchTinyRow';

interface GlPpeDetailProps {
    columnDefs: GlColumnDef[];
    rows: GlSheetRowVO[];
    summaryCurrentPeriod: Record<string, number>;
    summaryPriorYear: Record<string, number>;
    onRowClick?: (row: GlSheetRowVO, amount: number) => void;
    searchOpen?: boolean;
    searchQuery?: string;
    onSearchToggle?: () => void;
    onSearchQueryChange?: (value: string) => void;
}

function formatNum(v: number | null | undefined): string {
    if (v == null || v === 0) return '—';
    if (v < 0) {
        return `(${Math.abs(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })})`;
    }
    return v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

/** 该组期末余额（当期变动 + 上年），与 FS Summary 同源 */
function groupDisplayTotal(
    group: PpeGroup,
    summaryCurrentPeriod: Record<string, number>,
    summaryPriorYear: Record<string, number>,
): number {
    const keys = group.columnKeys;
    const movement = sumByKeys(summaryCurrentPeriod, keys);
    const prior = sumByKeys(summaryPriorYear, keys);
    return mergeGlRow6Display(movement, prior, 'BS-NCA-PPE');
}

function groupPriorTotal(group: PpeGroup, summaryPriorYear: Record<string, number>): number {
    return sumByKeys(summaryPriorYear, group.columnKeys);
}

/** 同一发票上除 PPE 外的非零科目；银行只保留 HKD / Ex HKD */
function buildPpeRelatedEntries(
    columnValues: Record<string, number> | undefined,
    columnDefs: GlColumnDef[],
): { amount: number; label: string }[] {
    const excludedBankKeys = buildExcludedBankSummaryKeys(columnDefs);
    const entries: { amount: number; label: string }[] = [];
    for (const [k, raw] of Object.entries(columnValues || {})) {
        const v = Number(raw) || 0;
        if (v === 0) continue;
        if (k === 'BS-NCA-PPE' || k.startsWith('BS-NCA-PPE|')) continue;
        if (excludedBankKeys.has(k)) continue;
        const def = columnDefs.find((d) => buildColumnKey(d) === k);
        if (def && isExRateColumn(def)) continue;
        const label = def?.nameEn || def?.fsNameEn || def?.fsCode
            || (k.includes('|') ? k.slice(0, k.indexOf('|')) : k);
        entries.push({ amount: v, label });
    }
    entries.sort((a, b) => Math.abs(b.amount) - Math.abs(a.amount));
    return entries;
}

function filterGroupRows(group: PpeGroup, rows: GlSheetRowVO[]): GlSheetRowVO[] {
    return rows.filter((row) => {
        for (const col of group.columns) {
            const val = row.columnValues?.[buildColumnKey(col)];
            if (val != null && val !== 0) return true;
        }
        return false;
    });
}

export function GlPpeDetail(props: GlPpeDetailProps) {
    const {
        columnDefs,
        rows,
        summaryCurrentPeriod,
        summaryPriorYear,
        onRowClick,
        searchOpen = false,
        searchQuery = '',
        onSearchToggle,
        onSearchQueryChange,
    } = props;

    const ppeGroups = useMemo(() => buildPpeGroups(columnDefs), [columnDefs]);
    const q = searchQuery.trim();

    // 有当期明细，或仅有上年余额的组都要展示；搜索时只保留命中行，合计仍用科目汇总
    const nonEmptyGroups = useMemo(() => {
        return ppeGroups
            .map((group) => {
                const groupRows = filterGroupRows(group, rows);
                const filteredRows = q
                    ? groupRows.filter((row) => tinyRowMatchesQuery(row, q))
                    : groupRows;
                return {
                    group,
                    filteredRows,
                    prior: groupPriorTotal(group, summaryPriorYear),
                    movement: sumByKeys(summaryCurrentPeriod, group.columnKeys),
                };
            })
            .filter(({ filteredRows, prior, movement }) =>
                q
                    ? filteredRows.length > 0
                    : filteredRows.length > 0 || prior !== 0 || movement !== 0);
    }, [ppeGroups, rows, summaryCurrentPeriod, summaryPriorYear, q]);

    const matchCount = useMemo(() => {
        if (!q) return null;
        return nonEmptyGroups.reduce((sum, g) => sum + g.filteredRows.length, 0);
    }, [nonEmptyGroups, q]);

    const ppeTotal = useMemo(() => {
        return resolveGlCurrentPeriodDisplay(
            'BS-NCA-PPE', summaryCurrentPeriod, summaryPriorYear, columnDefs,
        );
    }, [columnDefs, summaryCurrentPeriod, summaryPriorYear]);

    if (ppeGroups.length === 0) {
        return (
            <Box sx={{ p: 3, textAlign: 'center', color: 'text.secondary' }}>
                No PPE data
            </Box>
        );
    }

    return (
        <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 2, py: 1.5, gap: 1 }}>
                <Stack direction="row" alignItems="center" spacing={0.5} sx={{ minWidth: 0, flex: 1 }}>
                    <Box sx={{ minWidth: 0 }}>
                        <Typography variant="subtitle1" fontWeight={700}>
                            Property, plant and equipment
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            物業、廠房及設備 (BS-NCA-PPE) · Total = Current + Prior Year
                        </Typography>
                    </Box>
                    {onSearchToggle && onSearchQueryChange && (
                        <TinyRowSearch
                            open={searchOpen}
                            query={searchQuery}
                            matchCount={matchCount}
                            onToggle={onSearchToggle}
                            onQueryChange={onSearchQueryChange}
                        />
                    )}
                </Stack>
                <Typography variant="subtitle1" fontWeight={700}>
                    Total: {formatNum(ppeTotal)}
                </Typography>
            </Box>

            {nonEmptyGroups.length === 0 ? (
                <Typography sx={{ py: 2, color: 'text.secondary', textAlign: 'center' }}>
                    {q ? 'No matching rows' : 'No PPE data'}
                </Typography>
            ) : nonEmptyGroups.map(({ group, filteredRows }) => (
                <PpeGroupSection
                    key={group.groupKey}
                    group={group}
                    filteredRows={filteredRows}
                    summaryCurrentPeriod={summaryCurrentPeriod}
                    summaryPriorYear={summaryPriorYear}
                    columnDefs={columnDefs}
                    onRowClick={onRowClick}
                />
            ))}
        </Box>
    );
}

function PpeGroupSection({ group, filteredRows, summaryCurrentPeriod, summaryPriorYear, columnDefs, onRowClick }: {
    group: PpeGroup;
    filteredRows: GlSheetRowVO[];
    summaryCurrentPeriod: Record<string, number>;
    summaryPriorYear: Record<string, number>;
    columnDefs: GlColumnDef[];
    onRowClick?: (row: GlSheetRowVO, amount: number) => void;
}) {
    const visibleColumns = useMemo(
        () => filterNonEmptyPpeColumns(group, filteredRows, summaryCurrentPeriod, summaryPriorYear),
        [group, filteredRows, summaryCurrentPeriod, summaryPriorYear],
    );
    const displayGroup: PpeGroup = useMemo(() => {
        const cols = visibleColumns.length > 0 ? visibleColumns : group.columns;
        return {
            ...group,
            columns: cols,
            columnKeys: cols.map((c) => buildColumnKey(c)),
        };
    }, [group, visibleColumns]);

    const total = groupDisplayTotal(displayGroup, summaryCurrentPeriod, summaryPriorYear);
    const prior = groupPriorTotal(displayGroup, summaryPriorYear);
    const hasPriorRow = prior !== 0
        || displayGroup.columns.some((col) => resolvePriorYearAmount(buildColumnKey(col), summaryPriorYear) !== 0);

    const cellSx: React.CSSProperties = {
        padding: '3px 8px',
        fontSize: 12,
        whiteSpace: 'nowrap',
        borderRight: '1px solid #eee',
        borderBottom: '1px solid #eee',
    };
    const priorRowSx: React.CSSProperties = {
        ...cellSx,
        background: '#fff8e1',
        color: '#5d4037',
        fontStyle: 'italic',
    };

    return (
        <Accordion defaultExpanded disableGutters
            sx={{ '&:before': { display: 'none' }, boxShadow: 'none', borderTop: '1px solid #e0e0e0' }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}
                sx={{ minHeight: 36, '& .MuiAccordionSummary-content': { my: 0.5 } }}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="body2" fontWeight={600}>
                        {group.groupLabel}
                    </Typography>
                    {hasPriorRow && (
                        <Typography variant="caption" color="text.secondary">
                            includes Prior Year {formatNum(prior)}
                        </Typography>
                    )}
                </Box>
                <Typography variant="body2" fontWeight={600} sx={{ mr: 2 }}>
                    {formatNum(total)}
                </Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ p: 0 }}>
                <Box sx={{ overflow: 'auto' }}>
                    <table style={{ borderCollapse: 'collapse', width: '100%' }}>
                        <thead>
                            <tr style={{ background: '#fafafa' }}>
                                <th style={{ ...cellSx, fontWeight: 600, width: 30 }}>#</th>
                                <th style={{ ...cellSx, fontWeight: 600, width: 80 }}>Date</th>
                                <th style={{ ...cellSx, fontWeight: 600, width: 150 }}>Description</th>
                                <th style={{ ...cellSx, fontWeight: 600, width: 80 }}>Voucher</th>
                                {displayGroup.columns.map((col) => (
                                    <th key={col.glCode} style={{ ...cellSx, fontWeight: 600, minWidth: 80, textAlign: 'right' }}>
                                        {ppeColumnShortLabel(col)}
                                    </th>
                                ))}
                                <th style={{ ...cellSx, fontWeight: 600, minWidth: 180 }}>Related Entries</th>
                            </tr>
                        </thead>
                        <tbody>
                            {hasPriorRow && (
                                <tr>
                                    <td style={priorRowSx}>—</td>
                                    <td style={priorRowSx}></td>
                                    <td style={{ ...priorRowSx, maxWidth: 200 }}>Prior Year b/f</td>
                                    <td style={priorRowSx}></td>
                                    {displayGroup.columns.map((col) => {
                                        const val = resolvePriorYearAmount(buildColumnKey(col), summaryPriorYear);
                                        return (
                                            <td key={col.glCode} style={{ ...priorRowSx, textAlign: 'right' }}>
                                                {formatNum(val)}
                                            </td>
                                        );
                                    })}
                                    <td style={priorRowSx} />
                                </tr>
                            )}
                            {filteredRows.map((row, idx) => {
                                let rowAmt = 0;
                                for (const col of displayGroup.columns) {
                                    rowAmt += row.columnValues?.[buildColumnKey(col)] ?? 0;
                                }
                                const clickable = !!onRowClick && !!row.invoiceId;
                                return (
                                <tr
                                    key={row.invoiceId || idx}
                                    onClick={clickable ? () => onRowClick?.(row, rowAmt) : undefined}
                                    style={clickable ? {cursor: 'pointer'} : undefined}
                                    onMouseEnter={clickable ? (e) => { (e.currentTarget as HTMLTableRowElement).style.background = '#f5f5f5'; } : undefined}
                                    onMouseLeave={clickable ? (e) => { (e.currentTarget as HTMLTableRowElement).style.background = ''; } : undefined}
                                >
                                    <td style={cellSx}>{idx + 1}</td>
                                    <td style={cellSx}>{row.date || ''}</td>
                                    <td style={{ ...cellSx, maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                        {row.description || ''}
                                    </td>
                                    <td style={cellSx}>{row.voucherNo || ''}</td>
                                    {displayGroup.columns.map((col) => {
                                        const val = row.columnValues?.[buildColumnKey(col)];
                                        return (
                                            <td key={col.glCode} style={{ ...cellSx, textAlign: 'right' }}>
                                                {formatNum(val)}
                                            </td>
                                        );
                                    })}
                                    <td style={cellSx}>
                                        {buildPpeRelatedEntries(row.columnValues, columnDefs).map((entry, j, arr) => (
                                            <span key={j} style={{ marginRight: j < arr.length - 1 ? 12 : 0 }}>
                                                <span style={{ fontWeight: 600 }}>{formatNum(entry.amount)}</span>
                                                {' '}
                                                <span style={{ color: '#666' }}>{entry.label}</span>
                                            </span>
                                        ))}
                                    </td>
                                </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </Box>
            </AccordionDetails>
        </Accordion>
    );
}

export default GlPpeDetail;
